import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, TextField, SelectField, SelectItem, Button } from "@kuda/ui";
import SelectCluster from "../selectCluster";
import { config, getClusterProviders } from "../config";

const NAME_REGEX = /^[A-Za-z][A-Za-z0-9_-]*$/;

// sourceName may be a full Vault path (dev/kuda-internaltools/foo) — only the
// leaf segment is ever shown/edited/regex-validated here.
const leafOf = (fullName) => (fullName || "").split("/").pop();

/**
 * Lets the user adjust the target cluster/provider, environment, team, and
 * name before duplicating a secret — instead of a silent one-click copy.
 * `onConfirm` does the actual work and may throw; this dialog surfaces
 * whatever message it throws.
 */
const DuplicateSecretDialog = ({
  open,
  onClose,
  sourceName,
  defaultCluster,
  defaultEnvironment,
  defaultTeam,
  userRole,
  onConfirm,
}) => {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [targetCluster, setTargetCluster] = useState(defaultCluster || "");
  const [targetProvider, setTargetProvider] = useState("");
  const [environment, setEnvironment] = useState("");
  const [team, setTeam] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setName(`${leafOf(sourceName)}-copy`);
      setNameError("");
      setTargetCluster(defaultCluster || "");
      setEnvironment(defaultEnvironment || "");
      setTeam(defaultTeam || "");
      setError("");
    }
  }, [open, sourceName, defaultCluster, defaultEnvironment, defaultTeam]);

  const providersForCluster = useMemo(
    () => (targetCluster ? getClusterProviders(targetCluster) : []),
    [targetCluster]
  );

  useEffect(() => {
    setTargetProvider(providersForCluster[0]?.platformName || "");
  }, [providersForCluster]);

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    setNameError(value && !NAME_REGEX.test(value) ? "Must start with a letter, you can also add [0-9, -,_]" : "");
  };

  const targetVaultId = providersForCluster.find((p) => p.platformName === targetProvider)?.vaultId;
  const usesNestedVaultPath = targetProvider === "vault" && config.vaultNestedPathEngines.includes(targetVaultId);

  const canSubmit =
    !!name && !nameError && !!targetCluster && !!targetProvider &&
    (!usesNestedVaultPath || (!!environment && !!team));

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      const finalNewName = usesNestedVaultPath ? `${environment}/${team}/${name}` : name;
      await onConfirm({ newName: finalNewName, targetCluster, targetProvider, environment, team });
    } catch (err) {
      setError(err.response?.data?.error || err.message || `Failed to duplicate "${sourceName}".`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !loading) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Duplicate secret</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <TextField
            label="New name"
            value={name}
            onChange={handleNameChange}
            error={!!nameError}
            helperText={nameError || `Copies the value of "${sourceName}".`}
          />

          <SelectCluster
            key={`${open}-${sourceName}`}
            onClusterChange={setTargetCluster}
            label="Target cluster"
            defaultCluster={targetCluster}
            showAll={true}
            userRole={userRole}
          />

          {providersForCluster.length > 1 && (
            <SelectField
              label="Provider"
              placeholder="Select provider"
              value={targetProvider}
              onValueChange={setTargetProvider}
            >
              {providersForCluster.map((p) => (
                <SelectItem key={p.platformName} value={p.platformName}>
                  {p.platformName.toUpperCase()}
                </SelectItem>
              ))}
            </SelectField>
          )}

          <SelectField label="Environment" placeholder="Select environment" value={environment} onValueChange={setEnvironment}>
            {config.environments.map((env) => (
              <SelectItem key={env.value} value={env.value}>{env.label}</SelectItem>
            ))}
          </SelectField>

          <SelectField label="Team" placeholder="Select team" value={team} onValueChange={setTeam}>
            {config.projects.map((proj) => (
              <SelectItem key={proj.value} value={proj.value}>{proj.label}</SelectItem>
            ))}
          </SelectField>

          {error && <p className="text-sm text-color-text-state-error-default">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="text" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="filled" onClick={handleConfirm} loading={loading} disabled={!canSubmit}>
            Duplicate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateSecretDialog;
